/**
 * First-run onboarding: one-shot "pixels are live on your storefront" beacon.
 *
 * Only ever imported when the server injects
 * pmwDataLayer.general.first_event_confirmation, which happens exclusively on
 * fresh installs until the first confirmation is stored. Reports once which
 * pixels are active on this page and which vendor scripts were positively
 * observed loading, then the server-side flag disappears and this chunk is
 * never requested again.
 *
 * No visitor data leaves the site: pixel slugs, a consent-category snapshot
 * and the page type.
 */

export const initFirstEventConfirmation = () => {

	if (!pmwDataLayer?.general?.first_event_confirmation) return

	let sent = false

	/**
	 * Pixels whose vendor script load was positively detected. Everything
	 * else in the payload is "snippet injected" fidelity (the vendor snippet
	 * creates its own script tags, so there is no load event we own).
	 */
	const confirmedPixels = () => {

		const confirmed = []

		// Google: gtag.js (or its Tag Gateway proxy) completed loading through
		// pmw.loadScriptAndCacheIt, which records the URL only in onload.
		const loadedUrls = typeof pmw.getLoadedScriptUrls === "function" ? pmw.getLoadedScriptUrls() : []
		if (loadedUrls.some(url => url.includes("gtag/js") || url.includes("s=/gtag/js") || url.includes("s=%2Fgtag%2Fjs"))) {
			confirmed.push("google")
		}

		// Meta: the stub queues via fbq.queue; only the real fbevents.js
		// defines fbq.callMethod.
		if (window.fbq && typeof window.fbq.callMethod === "function") {
			confirmed.push("facebook")
		}

		return confirmed
	}

	const send = () => {

		if (sent) return

		const pixels = Object.keys(pmwDataLayer?.pixels || {})
		if (!pixels.length) return

		const consent = {
			marketing : !!pmw.consent?.categories?.get()?.marketing,
			statistics: !!pmw.consent?.categories?.get()?.statistics,
		}

		const confirmed = confirmedPixels()

		// A fully consent-blocked visit proves nothing; skip so a later
		// visit (typically the shop manager's own, after accepting the
		// banner) delivers a truthful confirmation instead.
		if (!confirmed.length && !consent.marketing && !consent.statistics) return

		sent = true

		const payload = JSON.stringify({
			pixels   : pixels,
			confirmed: confirmed,
			consent  : consent,
			page_type: pmwDataLayer?.shop?.page_type || "",
		})

		const url = pmw.root + "pmw/v1/onboarding/pixels-live/"

		// sendBeacon survives navigation; the Blob type becomes the
		// Content-Type so the REST route parses it as JSON.
		if (typeof navigator.sendBeacon === "function") {
			if (navigator.sendBeacon(url, new Blob([payload], {type: "application/json"}))) return
		}

		fetch(url, {
			method   : "POST",
			headers  : {"Content-Type": "application/json"},
			body     : payload,
			keepalive: true,
			cache    : "no-cache",
		}).catch(() => {})
	}

	// Give async vendor scripts (gtag.js) a moment to finish before the
	// regular send; leaving the page earlier still reports what we have.
	setTimeout(send, 3000)
	window.addEventListener("pagehide", send, {once: true})
}
